import { PrismicLinkContext } from '@monofront/types';
import { RichText } from 'prismic-reactjs';
import CustomPrismicHeading from '@components/CustomPrismicHeading';
import HyperLink from '@monofront/design-system/components/HyperLink';
import ColoredSlice from '@monofront/design-system/organisms/ColoredSlice';
import BubbleCard from '@monofront/design-system/components/BubbleCard';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import PrismicLink from '@components/PrismicLink';
import withSlice from '@screens/SlicesContainer/withSlice';
import { mapEditorialListAndBubble } from './mapEditorialListAndBubble';
import { IEditorialListAndBubble } from './types';

const EditorialListAndBubble: React.FC<IEditorialListAndBubble> = ({
  items,
  primary,
}) => {
  if (!primary || !items || !items.length) return null;

  const title = (
    <RichText
      render={primary.title}
      htmlSerializer={CustomPrismicHeading({
        className: 'catchy-title',
        after: primary.titleIcon?.url ? (
          <span className="title-icon">
            <ResponsiveImage
              width={primary.titleIcon.dimensions.width}
              height={primary.titleIcon.dimensions.height}
              src={primary.titleIcon.url}
              alt={primary.titleIcon.alt}
            />
          </span>
        ) : undefined,
      })}
    />
  );

  const leftContent = (
    <>
      {items.length ? (
        <ul className="tw-lf-bullet-list">
          {items.map((item, i) => (
            <li
              key={i}
              className={`tw-lf-bullet-list-item ${
                primary.listBulletpointColor
                  ? `before:tw-bg-[${primary.listBulletpointColor}`
                  : ''
              }`}
            >
              <RichText
                render={item.listItem}
                htmlSerializer={(type, _element, _text, children, index) =>
                  type === 'strong' ? (
                    <strong className="tw-lf-highlighted-white" key={index}>
                      {children}
                    </strong>
                  ) : null
                }
              />
            </li>
          ))}
        </ul>
      ) : null}
      {primary.ctaLink && primary.ctaLabel && (
        <PrismicLink
          className="left-column-cta"
          link={primary.ctaLink}
          trackingId={primary.ctaTrackingId || undefined}
        >
          <HyperLink variant="button-primary-span">
            {primary.ctaLabel}
          </HyperLink>
        </PrismicLink>
      )}
    </>
  );

  const bubbleCta =
    primary.bubbleCtaLink && primary.bubbleCtaLabel
      ? {
          link: primary.bubbleCtaLink,
          label: primary.bubbleCtaLabel,
          trackingId: primary.bubbleCtaTrackingId,
          context: PrismicLinkContext.SHOP,
        }
      : undefined;

  const rightContent = (
    <BubbleCard
      title={
        <>
          <span className="tw-lf-highlighted-yellow">
            {primary.bubbleFirstLineTitle}
          </span>
          <br />
          {primary.bubbleSecondLineTitle}
        </>
      }
      content={<RichText render={primary.bubbleDescription} />}
      cta={bubbleCta}
      sideText={primary.bubbleSidenote}
    />
  );

  return (
    <>
      <ColoredSlice
        backgroundColor={primary.backgroundColor}
        title={title}
        content={leftContent}
        secondContent={rightContent}
        UNSAFE_className="editorial-list-and-bubble"
      />
      <style jsx>{`
        :global(.editorial-list-and-bubble .left-column-cta) {
          display: inline-flex;
          margin-top: var(--space-4);
        }
        :global(.editorial-list-and-bubble .columns div + div) {
          text-align: center;
        }
      `}</style>
    </>
  );
};

export default withSlice(EditorialListAndBubble, mapEditorialListAndBubble);
